import { FiscalProvider, isEuCountry, PaymentMode, PaymentProvider } from './enums';

/**
 * Regras de onde cada PSP pode cobrar e de quais combinações PSP × modo o admin
 * oferece por país. Fonte única do admin (`MarketConfigEditDialog`) e das
 * Functions (`setMarketConfig`, `lib/billing/saleGateway.ts`).
 *
 * Origem: #756 — 2026-10-01 11:33 +0200 (A7 do Asaas).
 *
 * Por quê: o owner decidiu em 2026-10-01 que **o Asaas só pode cobrar no
 * Brasil** (a conta é da PJ brasileira, cobra só BRL e emite NFS-e) e que **o
 * Stripe da etiquetachef-srl (IT) é universal** — pode ser o PSP ativo de
 * qualquer país, UE e IT incluídas. A regra vivia num filtro da tela
 * (`p !== 'asaas'`) e em guards do callable que travavam a UE no Paddle; com
 * duas cópias, a tela e o servidor discordavam. Aqui ela existe uma vez.
 *
 * Usado por: admin — `apps/admin/src/features/billing/MarketConfigEditDialog.tsx`;
 * Functions — `admin/setMarketConfig.ts`, `lib/billing/saleGateway.ts`.
 *
 * Dicas: o servidor revalida tudo o que a tela filtra. Mudou um país aqui,
 * rode `marketPsp.test.ts` e a suíte do `setMarketConfig`.
 */

/** País único em que o Asaas pode cobrar (#756). */
export const ASAAS_ONLY_COUNTRY = 'BR';

/**
 * PSPs que o admin pode escolher como ativo de um mercado. Paddle, dLocal,
 * EBANX e Mercado Pago não têm checkout neste deploy e ficam fora do seletor;
 * `manual` não é PSP.
 */
export const MARKET_SELECTABLE_PROVIDERS = [PaymentProvider.STRIPE, PaymentProvider.ASAAS] as const;

/**
 * O PSP pode cobrar neste país? Só a geografia — se a chave existe e se o PSP
 * tem checkout é outra pergunta (`saleGateway.isAvailable`).
 *
 * Contrato: Asaas → só `BR`; Paddle → só UE (regra da Sprint 35, mantida);
 * qualquer outro (Stripe incluso) → todo país. Puro.
 */
export function providerAllowedInCountry(provider: string, country: string): boolean {
  if (provider === PaymentProvider.ASAAS) return country === ASAAS_ONLY_COUNTRY;
  if (provider === PaymentProvider.PADDLE) return isEuCountry(country);
  return true;
}

/** Uma opção do seletor "PSP ativo" do admin: PSP + modo. */
export type MarketPspChoice = {
  readonly provider: (typeof MARKET_SELECTABLE_PROVIDERS)[number];
  readonly mode: PaymentMode;
};

/**
 * As opções do seletor "PSP ativo" para um país: STRIPE e STRIPE SANDBOX em
 * todos; ASAAS e ASAAS SANDBOX só no BR.
 *
 * Dados: `marketPspChoices('BR')` → 4 opções; `marketPspChoices('IT')` → 2.
 */
export function marketPspChoices(country: string): MarketPspChoice[] {
  const out: MarketPspChoice[] = [];
  for (const provider of MARKET_SELECTABLE_PROVIDERS) {
    if (!providerAllowedInCountry(provider, country)) continue;
    out.push({ provider, mode: PaymentMode.LIVE }, { provider, mode: PaymentMode.SANDBOX });
  }
  return out;
}

/**
 * O modo do mercado, com o legado resolvido: doc sem `payment_mode` (todo doc
 * anterior à #756) é `live`, que era o único comportamento possível.
 */
export function marketPaymentMode(market: { readonly payment_mode?: PaymentMode | undefined }): PaymentMode {
  return market.payment_mode ?? PaymentMode.LIVE;
}

/**
 * O fiscal padrão de cada PSP ativo: Asaas → NFS-e do Asaas (G6 da #748);
 * Paddle → Paddle MoR; o resto (Stripe) → invoice do Stripe. O admin preenche
 * o fiscal por aqui ao trocar o PSP, e o `setMarketConfig` usa o mesmo valor
 * quando o fiscal não vem no input.
 *
 * Origem: #756 — 2026-10-01 11:33 +0200.
 */
export function defaultFiscalProviderFor(provider: string): FiscalProvider {
  if (provider === PaymentProvider.ASAAS) return FiscalProvider.ASAAS_NFSE;
  if (provider === PaymentProvider.PADDLE) return FiscalProvider.PADDLE_MOR;
  return FiscalProvider.STRIPE_HOSTED;
}

/** O catálogo como o seletor precisa: id do PSP, `enabled` antigo e os modos. */
export type PspCatalogEntry = {
  readonly id: string;
  readonly enabled: boolean;
  readonly modes?: { readonly live: { readonly enabled: boolean }; readonly sandbox: { readonly enabled: boolean } } | undefined;
};

/**
 * O PSP está ativo NESTE modo no catálogo (`/config/psps`)?
 *
 * Origem: #756 — 2026-10-01 13:31 +0200.
 *
 * Contrato: sem doc → inativo (o owner decidiu que o seletor do país mostra só
 * o que está ativo em `/psp`); doc sem `modes` (anterior à #756) → produção =
 * `enabled`, sandbox = desligado. Puro.
 */
export function pspModeEnabled(
  catalog: readonly PspCatalogEntry[],
  provider: string,
  mode: PaymentMode,
): boolean {
  const entry = catalog.find((c) => c.id === provider);
  if (!entry) return false;
  if (entry.modes) return entry.modes[mode].enabled;
  return mode === PaymentMode.LIVE ? entry.enabled : false;
}

/**
 * As opções do seletor "PSP ativo" de um país que estão LIGADAS em `/psp`:
 * `marketPspChoices(country)` (geografia: Asaas só no BR) ∩ modos ativos.
 * Fonte única da tela (`MarketPspFields`) e do servidor (`setMarketConfig`,
 * guard `psp_mode_disabled`).
 *
 * Origem: #756 — 2026-10-01 13:31 +0200.
 */
export function enabledPspChoices(country: string, catalog: readonly PspCatalogEntry[]): MarketPspChoice[] {
  return marketPspChoices(country).filter((c) => pspModeEnabled(catalog, c.provider, c.mode));
}
